import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, inject } from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import {SwUpdate, VersionReadyEvent} from '@angular/service-worker';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {RoutingStateService} from './services/routing-state.service';
import {MobileTokenRefreshService} from './auth/services/mobile-token-refresh.service';
import {TranslationService} from './services/translation.service';
import {ThemeService} from './services/theme.service';
import {AnalyticsService} from './services/analytics.service';
import {APP_CONFIG} from './config/app-config.token';
import {AppConfig} from './config/app-config.model';
import {MenuComponent} from './components/menu/menu.component';
import {FooterComponent} from './components/footer/footer.component';
import {SpinnerOverlayComponent} from './components/shared/spinner-overlay/spinner-overlay.component';
import {PlatformService} from './auth/services/platform.service';
import {MemberBannerComponent} from './navigation/member-banner/member-banner.component';
import {SidebarNavComponent} from './navigation/sidebar-nav/sidebar-nav.component';
import {BottomBarComponent} from './navigation/bottom-bar/bottom-bar.component';
import {LayoutService} from './navigation/layout.service';
import {TokenExpiryDialogComponent} from './auth/components/token-expiry-dialog/token-expiry-dialog.component';

// Backstop only. The service worker serves its cached shell, so a deploy is picked up when
// checkForUpdate() runs and not before. A timer alone left users up to six hours behind a
// release, and mobile made it worse: browsers throttle background timers and iOS suspends them,
// so the tick often did not fire until long after the app was foregrounded again. The
// visibilitychange check below is the primary trigger; this interval only covers a tab that
// stays visible for a long time.
const SW_UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;

@Component({
    selector: 'myabvv-root',
    templateUrl: './app.component.html',
    standalone: true,
    imports: [
      RouterOutlet, MenuComponent, FooterComponent, SpinnerOverlayComponent,
      MemberBannerComponent, SidebarNavComponent, BottomBarComponent, TokenExpiryDialogComponent
    ],
    // In desktop chrome we turn the app into a fixed-height shell: header, banner, sidebar
    // and footer stay put while only the content area scrolls.
    host: {
      '[class.l-app--shell]': '!layout.useMobileChrome() && showChrome()',
      '[class.l-app--collapsed]': 'layout.sidebarCollapsed()',
      '[class.l-app--mobile]': 'layout.useMobileChrome()',
    },
    styleUrl: './app.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit, OnDestroy {
  private routingState = inject(RoutingStateService);
  private mobileTokenRefreshService = inject(MobileTokenRefreshService);
  private translationService = inject(TranslationService);
  private themeService = inject(ThemeService);
  private analyticsService = inject(AnalyticsService);
  private swUpdate = inject(SwUpdate);
  private router = inject(Router);
  layout = inject(LayoutService);
  private config = inject<AppConfig>(APP_CONFIG);
  private platformService = inject(PlatformService);


  /** Whether to render the navigation chrome (hidden on pre-auth /auth pages). */
  readonly showChrome = signal(true);

  readonly isWeb: boolean;
  get showTokenExpiryDialog(): boolean {
    return this.isWeb;
  }


  private swUpdateIntervalId: ReturnType<typeof setInterval> | null = null;
  private swUpdateSubscription: Subscription | null = null;
  private swVisibilityListener: (() => void) | null = null;
  private routeSubscription: Subscription | null = null;

  constructor() {
    this.isWeb = !this.platformService.isMobile();
  }

  ngOnInit() {
    this.themeService.init();
    this.translationService.init();
    this.routingState.loadRouting();
    this.analyticsService.init();
    this.initServiceWorkerUpdates();
    this.initRouteFlags();

    if (!this.isWeb) {
      this.mobileTokenRefreshService.startTokenRefreshCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.swUpdateIntervalId !== null) {
      clearInterval(this.swUpdateIntervalId);
      this.swUpdateIntervalId = null;
    }

    if (this.swVisibilityListener !== null) {
      document.removeEventListener('visibilitychange', this.swVisibilityListener);
      this.swVisibilityListener = null;
    }

    if (this.swUpdateSubscription !== null) {
      this.swUpdateSubscription.unsubscribe();
      this.swUpdateSubscription = null;
    }

    if (this.routeSubscription !== null) {
      this.routeSubscription.unsubscribe();
      this.routeSubscription = null;
    }

    if (!this.isWeb) {
      this.mobileTokenRefreshService.stopTokenRefreshCheck();
    }
  }

  // Track the active route to toggle the nav chrome (hidden on the pre-auth /auth pages).
  private initRouteFlags(): void {
    this.updateRouteFlags(this.router.url);
    this.routeSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.updateRouteFlags(event.urlAfterRedirects));
  }

  private updateRouteFlags(url: string): void {
    // Chrome is hidden on the pre-auth /auth/* pages. This is a route-based proxy for
    // "authenticated": feature routes are protected by auth guards that bounce
    // unauthenticated users to /auth/landing, so they never reach a chrome-bearing route.
    this.showChrome.set(!url.startsWith('/auth'));
  }

  // Activate new SW versions as soon as they are ready so deploys are picked up
  // without forcing testers / users to manually hard-refresh.
  private initServiceWorkerUpdates(): void {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.swUpdateSubscription = this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.swUpdate.activateUpdate().then(() => document.location.reload());
      });

    // Primary trigger: ask whenever the app comes back to the foreground. That is the moment a
    // user returns to a tab or reopens the PWA after a release, and it is also the moment
    // background timers were frozen or throttled, so the interval below cannot be relied on.
    this.swVisibilityListener = () => {
      if (!document.hidden) {
        this.swUpdate.checkForUpdate();
      }
    };
    document.addEventListener('visibilitychange', this.swVisibilityListener);

    this.swUpdateIntervalId = setInterval(
      () => this.swUpdate.checkForUpdate(),
      SW_UPDATE_CHECK_INTERVAL_MS
    );
  }

}
